import { useState, useEffect } from "react";
import axios from "../../api/axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import './LoginSignup.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); 
  const [showPassword, setShowPassword] = useState(false); 
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); 
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [validToken, setValidToken] = useState(true);
  const [loading, setLoading] = useState(true);

  // New states for submit and success flow
  const [submitting, setSubmitting] = useState(false);
  const [successPhase, setSuccessPhase] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidToken(false);
      setLoading(false);
      setTimeout(() => navigate("/login"), 3000);
      return;
    }
  
    const verifyToken = async () => {
      try {
        const response = await axios.post("/api/auth/verify-reset-token", { token });
        if (!response.data.valid) {
          setValidToken(false);
          setTimeout(() => navigate("/login"), 3000);
        }
      } catch {
        setValidToken(false);
        setTimeout(() => navigate("/login"), 3000);
      } finally {
        setLoading(false);
      }
    };
  
    verifyToken();
  }, [token, navigate]);
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post("/api/auth/reset-password", { token, email, newPassword: password });
      // Hold spinner for 1s, then show message and freeze UI for 3s before redirect
      setTimeout(() => {
        setSubmitting(false);
        setSuccessPhase(true);
        setMessage(response.data.message);
        setTimeout(() => {
          setSuccessPhase(false);
          navigate("/login");
        }, 3000);
      }, 500);
    } catch (err) {
      setSubmitting(false);
      setError(err.response?.data?.error || "Something went wrong.");
    }
  };

  if (loading) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!validToken) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <p className="error">Invalid or expired token. Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (<>
          <Helmet>
      <title>Admin - Website Name</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <div className="auth-page-wrapper">
      <div className="auth-container">
        <h2>Reset Password</h2>
        <p>Return to <Link to="/login">Login</Link></p>

        {message && <p className="success" aria-live="polite">{message}</p>}
        {error && <p className="error" aria-live="polite">{error}</p>}

        <form onSubmit={handleSubmit} autoComplete="off">
          <label htmlFor="reset-email" className="sr-only">Enter your email</label>
          <input
            id="reset-email"
            type="email"
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={submitting || successPhase}
          />

          <div className="password-wrapper">
            <label htmlFor="new-password" className="sr-only">Enter new password</label>
            <input
              id="new-password"
              type={showPassword ? "text" : "password"}
              placeholder="New password"
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting || successPhase}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label="Toggle new password visibility"
              disabled={submitting || successPhase}
            >
              <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
            </button>
          </div>

          <div className="password-wrapper">
            <label htmlFor="confirm-password" className="sr-only">Confirm your new password</label>
            <input
              id="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm new password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={submitting || successPhase}
            />
            <button
              type="button"
              className="toggle-password"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              aria-label="Toggle confirm password visibility"
              disabled={submitting || successPhase}
            >
              <i className={`fa-solid ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting || successPhase}
          >
            {submitting
              ? <div className="spinner"></div>
              : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default ResetPassword;
